/**
 * Builds a deterministic bundle-instance identifier from a list of items.
 *
 * The result is prefixed with the `bundleId` so that two different bundle
 * types containing identical child products never collide.  Each item then
 * contributes `productId-itemVariationId-specs` where specs are a sorted,
 * comma-separated list of `specId:optionId` pairs.  The per-item segments
 * are sorted lexicographically and joined with `|` so that item ordering
 * does not affect the result.
 *
 * This helper is shared between the add-to-cart flow (which stamps the id
 * before sending items to the API) and the display selector (which
 * reconstructs the id when the API does not round-trip it).
 */
export function buildBundleInstanceId(
  bundleId: number | string,
  items: ReadonlyArray<{
    productId: string;
    itemVariationId?: number | string;
    specs?: ReadonlyArray<{specId?: string; optionId?: string}>;
  }>,
): string {
  const itemsPart = items
    .map(i => {
      const specsPart = (i.specs ?? [])
        .map(s => `${s.specId ?? ''}:${s.optionId ?? ''}`)
        .sort()
        .join(',');
      return `${i.productId}-${i.itemVariationId ?? ''}-${specsPart}`;
    })
    .sort()
    .join('|');
  return `${bundleId}::${itemsPart}`;
}
